import type { MouseEventHandler } from 'react';
import type { Campaign } from 'content/campaigns/types';
import { pushToDataLayer } from 'components/shared/UserAnalytics/utils';

export type HomepageCtaAttrs = {
  'data-buttontype': string;
  'data-buttontype-v3'?: string;
  'data-buttontype-legacy'?: string;
  onClick?: MouseEventHandler<HTMLElement>;
};

function asAliasList(
  legacyButtonType?: string | readonly string[],
): string[] {
  if (!legacyButtonType) return [];
  return (Array.isArray(legacyButtonType)
    ? [...legacyButtonType]
    : [legacyButtonType]
  ).filter(Boolean);
}

/**
 * GTM click tags in this codebase match `[data-buttontype="…"]` on the
 * clicked node. When a live CTA replaces an old one, the **legacy** name
 * goes on `data-buttontype` so existing tags still fire. The v3 name is
 * kept on `data-buttontype-v3`. Extra aliases are dataLayer-pushed.
 */
export function homepageCtaProps(
  buttonType: string,
  legacyButtonType?: string | readonly string[],
): HomepageCtaAttrs {
  const aliases = asAliasList(legacyButtonType);
  const gtmName = aliases[0] ?? buttonType;
  const extraAliases = aliases.slice(1);
  const pushNames = [
    ...(buttonType !== gtmName ? [buttonType] : []),
    ...extraAliases,
  ];

  return {
    'data-buttontype': gtmName,
    ...(buttonType !== gtmName ? { 'data-buttontype-v3': buttonType } : {}),
    ...(aliases.length
      ? { 'data-buttontype-legacy': aliases.join(' ') }
      : {}),
    ...(pushNames.length
      ? {
          onClick: () => {
            pushNames.forEach((name) => {
              pushToDataLayer('button_click', { buttonType: name });
            });
          },
        }
      : {}),
  };
}

const CAMPAIGN_HERO_CTA: Record<
  Campaign['product'],
  { buttonType: string; legacy: string }
> = {
  ring: {
    buttonType: 'campaign_hero_ring_cta',
    legacy: 'ring_banner_buy_button',
  },
  'ring-pro': {
    buttonType: 'campaign_hero_ring_pro_cta',
    legacy: 'ring_pro_banner_buy_button',
  },
  home: {
    buttonType: 'campaign_hero_home_cta',
    legacy: 'home_banner_buy_button',
  },
  m1: {
    buttonType: 'campaign_hero_m1_cta',
    legacy: 'm1_banner_buy_button',
  },
  cgm: {
    buttonType: 'campaign_hero_cgm_cta',
    legacy: 'm1_banner_buy_button',
  },
  'blood-vision': {
    buttonType: 'campaign_hero_blood_vision_cta',
    legacy: 'bloodvision_banner_buy_button',
  },
  'gift-card': {
    buttonType: 'campaign_hero_gift_card_cta',
    legacy: 'gift card cta',
  },
};

export function campaignHeroCtaProps(
  product?: Campaign['product'],
): HomepageCtaAttrs {
  if (!product) {
    return { 'data-buttontype': 'campaign_hero_cta' };
  }
  const mapped = CAMPAIGN_HERO_CTA[product];
  if (!mapped) {
    return { 'data-buttontype': `campaign_hero_${product}_cta` };
  }
  return homepageCtaProps(mapped.buttonType, mapped.legacy);
}
